import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Clock, AlertTriangle, ChevronLeft, ChevronRight, Flag, Send,
  CheckCircle, Circle, Loader2, Eye, EyeOff, AlertCircle, Shield,
  Maximize, Monitor, Camera, X, Timer, BookOpen, Target, Play, FileText,
  Upload, Download, File, ExternalLink, Lock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AIProctoringCamera from '../components/AIProctoringCamera';
import AudioProctoring from '../components/AudioProctoring';
import Editor from '@monaco-editor/react';
import useTestScreenLock from '../hooks/useTestScreenLock';

const API = process.env.REACT_APP_BACKEND_URL;

const EnterpriseAssessmentPage = () => {
  const { challengeId, roundId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attemptData, setAttemptData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  
  // Instructions modal state
  const [showInstructions, setShowInstructions] = useState(true);
  const [testStarted, setTestStarted] = useState(false);
  const [challengeInfo, setChallengeInfo] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Code execution state
  const [codeLanguage, setCodeLanguage] = useState('python');
  const [codeRunning, setCodeRunning] = useState(false);
  const [codeOutput, setCodeOutput] = useState(null);
  const [customInput, setCustomInput] = useState('');
  const [showTestCases, setShowTestCases] = useState(true);
  
  // Proctoring state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [violations, setViolations] = useState([]);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [aiProctoringEnabled, setAiProctoringEnabled] = useState(true);
  const [aiViolations, setAiViolations] = useState(0);
  const [audioViolations, setAudioViolations] = useState(0);
  const [audioSensitivity, setAudioSensitivity] = useState('moderate');
  
  // PDF answer upload state
  const [pdfAnswerFiles, setPdfAnswerFiles] = useState({});
  const [uploadingPdf, setUploadingPdf] = useState(false);
  
  // Navigation setting - controls whether candidates can navigate between questions
  const [allowNavigation, setAllowNavigation] = useState(true);
  // Lock answers setting - when enabled, answers are locked when moving to next question
  const [lockAnswersOnProceed, setLockAnswersOnProceed] = useState(false);
  // Track which questions are locked (their answers cannot be changed)
  const [lockedQuestions, setLockedQuestions] = useState({});
  
  const timerRef = useRef(null);
  const violationCountRef = useRef(0);

  // Lock the test screen when assessment is active (not showing instructions and not submitted)
  const isTestActive = testStarted && attemptData && !submitted && !showInstructions;
  useTestScreenLock(isTestActive);

  // Fetch challenge info for instructions
  useEffect(() => {
    const fetchChallengeInfo = async () => {
      try {
        const res = await axios.get(`${API}/api/enterprise/challenges/${challengeId}`);
        setChallengeInfo(res.data);
      } catch (err) {
        console.error('Failed to fetch challenge info:', err);
      }
    };
    fetchChallengeInfo();
  }, [challengeId]);

  useEffect(() => {
    if (!email) {
      setError('Email is required to start assessment');
      setLoading(false);
      return;
    }
    // Only start assessment when user clicks "Start Assessment" button
    if (testStarted) {
      startAssessment();
    } else {
      setLoading(false);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [challengeId, roundId, email, testStarted]);

  // Setup proctoring listeners
  useEffect(() => {
    if (attemptData) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      document.addEventListener('fullscreenchange', handleFullscreenChange);
      document.addEventListener('contextmenu', handleRightClick);
      document.addEventListener('copy', handleCopyPaste);
      document.addEventListener('paste', handleCopyPaste);
      
      // Request fullscreen
      requestFullscreen();
    }
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('contextmenu', handleRightClick);
      document.removeEventListener('copy', handleCopyPaste);
      document.removeEventListener('paste', handleCopyPaste);
    };
  }, [attemptData]);

  // Timer
  useEffect(() => {
    if (attemptData && timeLeft > 0 && !submitted) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSubmit(true); // Auto-submit
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [attemptData, submitted]);

  const requestFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen().catch(() => {});
    }
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      logViolation('tab_switch', 'high', 'Switched to another tab/window');
    }
  };

  const handleFullscreenChange = () => {
    if (!document.fullscreenElement) {
      setIsFullscreen(false);
      logViolation('fullscreen_exit', 'medium', 'Exited fullscreen mode');
    } else {
      setIsFullscreen(true);
    }
  };

  const handleRightClick = (e) => {
    e.preventDefault();
    logViolation('right_click', 'low', 'Attempted right-click');
  };

  const handleCopyPaste = (e) => {
    e.preventDefault();
    logViolation('copy_paste', 'medium', 'Attempted copy/paste');
  };

  const logViolation = async (type, severity, details) => {
    violationCountRef.current += 1;
    setViolations(prev => [...prev, { type, severity, details, timestamp: new Date().toISOString() }]);
    
    // Show warning
    setWarningMessage(`Warning: ${details}. Violation ${violationCountRef.current}/10`);
    setShowWarning(true);
    setTimeout(() => setShowWarning(false), 3000);
    
    // Log to backend
    if (attemptData) {
      try {
        const res = await axios.post(`${API}/api/enterprise/${challengeId}/proctoring/log-violation`, null, {
          params: {
            attempt_id: attemptData.attempt_id,
            violation: JSON.stringify({ type, severity, details })
          }
        });
        
        if (res.data.auto_terminated) {
          toast.error('Assessment terminated due to too many violations');
          setSubmitted(true);
          setResult({ terminated: true });
        }
      } catch (err) {
        console.error('Failed to log violation:', err);
      }
    }
  };

  // Handle AI proctoring violations
  const handleAIViolation = useCallback((violation) => {
    setAiViolations(prev => prev + 1);
    logViolation(`ai_${violation.type}`, 'high', violation.message);
  }, []);

  const handleAIProctoringStatus = useCallback((status) => {
    if (status.isTerminated) {
      toast.error('Assessment terminated due to proctoring violations');
      setSubmitted(true);
      setResult({ terminated: true, reason: 'ai_proctoring' });
    }
  }, []);

  // Handle Audio proctoring violations
  const handleAudioViolation = useCallback((violation) => {
    setAudioViolations(prev => prev + 1);
    logViolation(`audio_${violation.type}`, 'medium', violation.message);
  }, []);

  const handleAudioStatus = useCallback((status) => {
    if (status.isTerminated) {
      toast.error('Assessment terminated due to audio violations');
      setSubmitted(true);
      setResult({ terminated: true, reason: 'audio_proctoring' });
    }
  }, []);

  const startAssessment = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        `${API}/api/enterprise/${challengeId}/start-assessment/${roundId}?email=${encodeURIComponent(email)}`
      );
      
      setAttemptData(res.data);
      setQuestions(res.data.questions || []);
      
      // Set navigation mode from round settings (default to true for backward compatibility)
      // Explicitly check for false to disable navigation
      const navEnabled = res.data.allow_navigation === true || res.data.allow_navigation === undefined || res.data.allow_navigation === null;
      setAllowNavigation(navEnabled);
      console.log('[Assessment] Navigation mode:', navEnabled ? 'FREE' : 'SEQUENTIAL', '- Raw value:', res.data.allow_navigation);
      
      // Set lock answers mode from round settings
      setLockAnswersOnProceed(res.data.lock_answers_on_proceed || false);
      
      // Calculate time left
      const endTime = new Date(res.data.end_time);
      const now = new Date();
      const secondsLeft = Math.max(0, Math.floor((endTime - now) / 1000));
      setTimeLeft(secondsLeft);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    // Check if the question is locked
    if (lockedQuestions[questionId]) {
      return; // Don't allow changes to locked questions
    }
    
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
    
    // Auto-save answer
    saveAnswer(questionId, answer);
  };

  // Function to handle moving to the next question with optional locking
  const moveToNextQuestion = () => {
    if (currentIndex >= questions.length - 1) return;
    
    // If lock mode is enabled and we're in sequential mode, lock the current question
    if (lockAnswersOnProceed && !allowNavigation) {
      const currentQuestionId = questions[currentIndex]?.id;
      if (currentQuestionId) {
        setLockedQuestions(prev => ({ ...prev, [currentQuestionId]: true }));
      }
    }
    
    setCurrentIndex(currentIndex + 1);
  };

  // Function to handle moving to a specific question (for navigator clicks)
  const moveToQuestion = (targetIndex) => {
    if (targetIndex === currentIndex) return;
    
    // In free navigation mode, just move directly
    if (allowNavigation) {
      setCurrentIndex(targetIndex);
      return;
    }
    
    // In sequential mode, can ONLY go to the next question (targetIndex === currentIndex + 1)
    // Jumping ahead or going back is not allowed
    if (targetIndex === currentIndex + 1) {
      // If lock mode is enabled, lock the current question
      if (lockAnswersOnProceed) {
        const currentQId = questions[currentIndex]?.id;
        if (currentQId) {
          setLockedQuestions(prev => ({ ...prev, [currentQId]: true }));
        }
      }
      setCurrentIndex(targetIndex);
    }
    // Any other navigation (back or jump ahead) is blocked in sequential mode
  };

  // Handle PDF answer file upload for PDF questions
  const handlePdfAnswerUpload = async (questionId, file) => {
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setUploadingPdf(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('question_id', questionId);
    formData.append('attempt_id', attemptData?.attempt_id || '');
    
    try {
      const res = await axios.post(
        `${API}/api/enterprise/${challengeId}/upload-pdf-answer`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (res.data.success) {
        setPdfAnswerFiles(prev => ({
          ...prev,
          [questionId]: {
            filename: file.name,
            url: res.data.file_url,
            uploaded_at: new Date().toISOString()
          }
        }));
        
        // Also save as answer reference
        handleAnswerChange(questionId, `[PDF Upload: ${file.name}]`);
        toast.success('Answer uploaded successfully');
      }
    } catch (err) {
      console.error('PDF upload failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload answer');
    } finally {
      setUploadingPdf(false);
    }
  };

  const saveAnswer = useCallback(async (questionId, answer) => {
    if (!attemptData) return;
    
    try {
      // Send answer in request body for reliability
      await axios.post(
        `${API}/api/enterprise/${challengeId}/submit-answer?attempt_id=${attemptData.attempt_id}&question_id=${questionId}`,
        { answer, time_taken: 0 },
        { headers: { 'Content-Type': 'application/json' } }
      );
      console.log(`[SaveAnswer] Saved answer for question ${questionId}`);
    } catch (err) {
      console.error('Failed to save answer:', err);
      // Retry with query param as fallback
      try {
        await axios.post(
          `${API}/api/enterprise/${challengeId}/submit-answer`,
          null,
          {
            params: {
              attempt_id: attemptData.attempt_id,
              question_id: questionId,
              answer: JSON.stringify({ answer, time_taken: 0 })
            }
          }
        );
        console.log(`[SaveAnswer] Saved answer via fallback for question ${questionId}`);
      } catch (fallbackErr) {
        console.error('Fallback save also failed:', fallbackErr);
      }
    }
  }, [attemptData, challengeId]);

  const toggleFlag = (questionId) => {
    setFlagged(prev => ({ ...prev, [questionId]: !prev[questionId] }));
  };

  // Run code against test cases or custom input
  const runCode = async (runType = 'test') => {
    if (!currentQuestion || currentQuestion.type !== 'coding') return;
    if (!answers[currentQuestion.id]?.trim()) {
      toast.error('Please write some code first');
      return;
    }
    
    setCodeRunning(true);
    setCodeOutput(null);
    
    try {
      const payload = {
        source_code: answers[currentQuestion.id],
        language: codeLanguage,
        stdin: runType === 'custom' ? customInput : '',
        test_cases: runType === 'test' && currentQuestion.test_cases?.length > 0
          ? currentQuestion.test_cases.slice(0, 5).map(tc => ({
              input: tc.input || '',
              expected_output: tc.expected_output || '',
              is_hidden: tc.is_hidden || false
            }))
          : null
      };
      
      const res = await axios.post(
        `${API}/api/enterprise/${challengeId}/run-code`,
        payload
      );
      
      setCodeOutput(res.data);
      
      if (res.data.all_passed) {
        toast.success(`All ${res.data.passed_count} test cases passed!`);
      } else if (res.data.results) {
        toast.info(`${res.data.passed_count}/${res.data.total_count} test cases passed`);
      } else if (res.data.success) {
        toast.success('Code executed successfully');
      } else {
        toast.error(`Execution failed: ${res.data.status}`);
      }
    } catch (err) {
      setCodeOutput({
        success: false,
        error: err.response?.data?.detail || 'Failed to execute code',
        status: 'Error'
      });
      toast.error('Code execution failed');
    } finally {
      setCodeRunning(false);
    }
  };

  const handleSubmit = async (isAutoSubmit = false) => {
    if (!isAutoSubmit && !window.confirm('Are you sure you want to submit? You cannot change answers after submission.')) {
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/enterprise/${challengeId}/submit-assessment/${attemptData.attempt_id}`
      );
      
      setSubmitted(true);
      setResult(res.data);
      toast.success('Assessment submitted successfully!');
      
      // Exit fullscreen
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const currentQuestion = questions[currentIndex];
  const answeredCount = Object.keys(answers).length;
  const flaggedCount = Object.values(flagged).filter(Boolean).length;

  // Get round info from challenge
  const roundInfo = challengeInfo?.rounds?.find(r => r.id === roundId);
  const proctoringSettings = challengeInfo?.proctoring_settings || {};

  // Instructions Modal - Show before test starts
  if (showInstructions && !testStarted && !error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-6">
        <div className="max-w-3xl w-full bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-8 py-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <BookOpen size={28} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Assessment Instructions</h1>
                <p className="text-cyan-200 text-sm mt-1">{challengeInfo?.title || 'Enterprise Assessment'}</p>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-8 max-h-[60vh] overflow-y-auto">
            {/* Test Info */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-white/5 rounded-2xl p-5 text-center border border-white/10">
                <Clock size={28} className="mx-auto text-cyan-400 mb-2" />
                <p className="text-2xl font-bold text-white">{roundInfo?.duration_minutes || 30}</p>
                <p className="text-slate-400 text-sm">Minutes</p>
              </div>
              <div className="bg-white/5 rounded-2xl p-5 text-center border border-white/10">
                <FileText size={28} className="mx-auto text-emerald-400 mb-2" />
                <p className="text-2xl font-bold text-white">{roundInfo?.total_marks || '—'}</p>
                <p className="text-slate-400 text-sm">Total Marks</p>
              </div>
              <div className="bg-white/5 rounded-2xl p-5 text-center border border-white/10">
                <Target size={28} className="mx-auto text-amber-400 mb-2" />
                <p className="text-2xl font-bold text-white">{roundInfo?.cutoff_marks || '—'}</p>
                <p className="text-slate-400 text-sm">Passing Marks</p>
              </div>
            </div>
            
            {/* Instructions List */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <AlertCircle size={20} className="text-amber-400" />
                Important Instructions
              </h3>
              <ul className="space-y-3">
                {[
                  'This is a proctored assessment. Your activity will be monitored throughout the test.',
                  'Ensure you have a stable internet connection before starting.',
                  'The timer will start as soon as you click "Start Assessment" and cannot be paused.',
                  'Do not switch tabs or windows during the test. This will be recorded as a violation.',
                  'Do not use any external resources, notes, or assistance during the assessment.',
                  'Your webcam may be used for identity verification and proctoring.',
                  'The test will auto-submit when the timer reaches zero.',
                  'You can flag questions for review and navigate between questions freely.',
                  'Make sure to submit your test before the time expires.'
                ].map((instruction, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-slate-300">
                    <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5">
                      {idx + 1}
                    </span>
                    <span className="leading-relaxed">{instruction}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Proctoring Notice */}
            <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-2xl p-5 mb-6">
              <h4 className="text-amber-400 font-semibold mb-3 flex items-center gap-2">
                <Shield size={18} />
                Proctoring Features Active
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {proctoringSettings.fullscreen_enforcement !== false && (
                  <div className="flex items-center gap-2 text-slate-300">
                    <Maximize size={14} className="text-amber-400" />
                    <span>Fullscreen Mode Required</span>
                  </div>
                )}
                {proctoringSettings.tab_switch_detection !== false && (
                  <div className="flex items-center gap-2 text-slate-300">
                    <Monitor size={14} className="text-amber-400" />
                    <span>Tab Switch Detection</span>
                  </div>
                )}
                {proctoringSettings.copy_paste_restriction !== false && (
                  <div className="flex items-center gap-2 text-slate-300">
                    <X size={14} className="text-amber-400" />
                    <span>Copy/Paste Disabled</span>
                  </div>
                )}
                {proctoringSettings.webcam_proctoring && (
                  <div className="flex items-center gap-2 text-slate-300">
                    <Camera size={14} className="text-amber-400" />
                    <span>Webcam Monitoring</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Agreement Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                agreedToTerms 
                  ? 'bg-cyan-500 border-cyan-500' 
                  : 'border-slate-500 group-hover:border-slate-400'
              }`}>
                {agreedToTerms && <CheckCircle size={16} className="text-white" />}
              </div>
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="sr-only"
              />
              <span className="text-slate-300 leading-relaxed">
                I have read and understood all the instructions. I agree to follow the assessment guidelines and understand that any violation may result in disqualification.
              </span>
            </label>
          </div>
          
          {/* Footer */}
          <div className="px-8 py-6 bg-slate-900/50 border-t border-white/10 flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 text-slate-400 hover:text-white transition-colors font-medium"
            >
              ← Go Back
            </button>
            <button
              onClick={() => {
                if (agreedToTerms) {
                  setShowInstructions(false);
                  setTestStarted(true);
                  setLoading(true);
                }
              }}
              disabled={!agreedToTerms}
              className={`px-8 py-3.5 rounded-xl font-semibold flex items-center gap-3 transition-all ${
                agreedToTerms
                  ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 text-white hover:from-emerald-500 hover:to-emerald-400 shadow-lg shadow-emerald-500/20'
                  : 'bg-slate-700 text-slate-400 cursor-not-allowed'
              }`}
            >
              <Play size={20} />
              Start Assessment
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center border border-cyan-500/20">
            <Loader2 className="animate-spin text-cyan-400" size={40} />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Loading Assessment</h2>
          <p className="text-slate-400">Preparing your test environment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-6">
        <div className="text-center max-w-md bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/10 rounded-3xl p-10">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
            <AlertCircle size={40} className="text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Unable to Load Assessment</h1>
          <p className="text-slate-400 text-lg leading-relaxed mb-8">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-8 py-3.5 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl font-semibold hover:from-cyan-500 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/20"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-6">
        <div className="text-center max-w-lg bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/10 rounded-3xl p-10">
          {result?.terminated ? (
            <>
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle size={48} className="text-red-400" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-3">Assessment Terminated</h1>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                Your assessment was terminated due to excessive proctoring violations.
              </p>
            </>
          ) : result?.results_visible === false ? (
            <>
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                <CheckCircle size={48} className="text-cyan-400" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-3">Assessment Submitted!</h1>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                {result?.message_to_candidate || "Your responses have been recorded successfully. Results will be announced by the organizer."}
              </p>
            </>
          ) : (
            <>
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center">
                <CheckCircle size={48} className="text-emerald-400" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-3">Assessment Submitted!</h1>
              <p className="text-slate-400 text-lg leading-relaxed mb-6">
                Your responses have been recorded successfully.
              </p>
              
              {result && (
                <div className="bg-white/5 rounded-2xl p-6 mb-8 border border-white/10">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center p-4 bg-white/5 rounded-xl">
                      <p className="text-slate-500 text-sm uppercase tracking-wider mb-1">Your Score</p>
                      <p className="text-3xl font-bold text-white">{result.score}<span className="text-lg text-slate-500">/{result.total_points}</span></p>
                    </div>
                    <div className="text-center p-4 bg-white/5 rounded-xl">
                      <p className="text-slate-500 text-sm uppercase tracking-wider mb-1">Percentage</p>
                      <p className="text-3xl font-bold text-white">{result.percentage}<span className="text-lg text-slate-500">%</span></p>
                    </div>
                  </div>
                  {result.passed !== undefined && (
                    <div className={`mt-6 p-4 rounded-xl text-lg font-semibold ${
                      result.passed 
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                        : 'bg-red-500/20 text-red-400 border border-red-500/30'
                    }`}>
                      {result.passed ? '✓ Congratulations! You Passed' : '✗ Did Not Meet Passing Criteria'}
                    </div>
                  )}
                  {result.needs_manual_review && (
                    <p className="mt-4 text-sm text-slate-400">
                      Some answers require manual review. Final score may be updated.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
          
          <button
            onClick={() => navigate(`/enterprise/${challengeId}`)}
            className="px-8 py-3.5 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl font-semibold hover:from-cyan-500 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/20"
          >
            Return to Challenge
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex flex-col ${isTestActive ? 'test-screen-container' : ''}`} data-testid="enterprise-assessment-page">
      {/* Proctoring Warning - Enhanced visibility */}
      {showWarning && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-gradient-to-r from-red-600 to-red-500 text-white px-8 py-4 rounded-2xl shadow-2xl shadow-red-500/30 flex items-center gap-4 animate-pulse border border-red-400/30">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <AlertTriangle size={24} />
          </div>
          <span className="text-lg font-semibold tracking-wide">{warningMessage}</span>
        </div>
      )}
      
      {/* Minimal Clean Header - Distraction-free */}
      <header data-test-header className="bg-slate-900/95 backdrop-blur-xl border-b border-white/5 px-6 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Left: Logo + Test Name */}
          <div className="flex items-center gap-5">
            {/* EvalentX Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/20">
                <BookOpen size={22} className="text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                EvalentX
              </span>
            </div>
            
            {/* Divider */}
            <div className="w-px h-8 bg-white/10" />
            
            {/* Test Name */}
            <div>
              <h1 className="text-lg font-semibold text-white tracking-wide">{attemptData?.round_name}</h1>
              <p className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5">
                <Shield size={12} className="text-cyan-400" />
                <span>Proctored Assessment</span>
              </p>
            </div>
          </div>
          
          {/* Right: Timer, Stats, Submit */}
          <div className="flex items-center gap-5">
            {/* Timer - Enhanced visibility */}
            <div className={`flex items-center gap-3 px-5 py-2.5 rounded-xl font-mono ${
              timeLeft < 300 
                ? 'bg-gradient-to-r from-red-600/20 to-red-500/20 border border-red-500/30 text-red-400' 
                : timeLeft < 600
                ? 'bg-gradient-to-r from-amber-600/20 to-amber-500/20 border border-amber-500/30 text-amber-400'
                : 'bg-white/5 border border-white/10 text-white'
            }`}>
              <Timer size={20} className={timeLeft < 300 ? 'animate-pulse' : ''} />
              <span className="text-xl font-bold tracking-wider">{formatTime(timeLeft)}</span>
            </div>
            
            {/* Progress Stats */}
            <div className="flex items-center gap-4 px-4 py-2 bg-white/5 rounded-xl border border-white/10">
              <div className="text-center">
                <div className="text-lg font-bold text-emerald-400">{answeredCount}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">Answered</div>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div className="text-center">
                <div className="text-lg font-bold text-slate-300">{questions.length}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">Total</div>
              </div>
              {flaggedCount > 0 && (
                <>
                  <div className="w-px h-8 bg-white/10" />
                  <div className="text-center">
                    <div className="text-lg font-bold text-amber-400">{flaggedCount}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">Flagged</div>
                  </div>
                </>
              )}
            </div>
            
            {/* Submit Button */}
            <button
              onClick={() => handleSubmit(false)}
              disabled={submitting}
              className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-xl font-semibold flex items-center gap-2 hover:from-emerald-500 hover:to-emerald-400 disabled:opacity-50 shadow-lg shadow-emerald-500/20 transition-all hover:shadow-emerald-500/30 hover:scale-[1.02]"
            >
              {submitting ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
              <span className="tracking-wide">Submit Test</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        {/* Question Panel - Enhanced readability */}
        <main className="flex-1 p-8 overflow-y-auto">
          {currentQuestion && (
            <div className="max-w-4xl mx-auto">
              {/* Question Header - Improved spacing */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <span className="px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-300 rounded-xl text-base font-bold border border-cyan-500/20">
                    Question {currentIndex + 1}
                  </span>
                  <span className="px-3 py-1.5 bg-white/5 rounded-lg text-slate-300 text-sm font-medium border border-white/10">
                    {currentQuestion.points || 1} {(currentQuestion.points || 1) !== 1 ? 'points' : 'point'}
                  </span>
                  <span className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
                    currentQuestion.difficulty === 'hard' 
                      ? 'bg-red-500/10 text-red-400 border-red-500/20' 
                      : currentQuestion.difficulty === 'medium' 
                      ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' 
                      : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                  }`}>
                    {currentQuestion.difficulty ? currentQuestion.difficulty.charAt(0).toUpperCase() + currentQuestion.difficulty.slice(1) : 'Medium'}
                  </span>
                  {/* Locked indicator */}
                  {lockedQuestions[currentQuestion.id] && (
                    <span className="px-3 py-1.5 bg-orange-500/10 text-orange-400 rounded-lg text-sm font-medium border border-orange-500/20 flex items-center gap-1.5">
                      <Lock size={14} />
                      Answer Locked
                    </span>
                  )}
                </div>
                
                <button
                  onClick={() => toggleFlag(currentQuestion.id)}
                  className={`p-3 rounded-xl transition-all border ${
                    flagged[currentQuestion.id] 
                      ? 'bg-amber-500/20 text-amber-400 border-amber-500/30 shadow-lg shadow-amber-500/10' 
                      : 'bg-white/5 text-slate-400 border-white/10 hover:text-white hover:border-white/20'
                  }`}
                  title={flagged[currentQuestion.id] ? 'Unflag question' : 'Flag for review'}
                >
                  <Flag size={22} />
                </button>
              </div>
              
              {/* Question Content - ENHANCED READABILITY */}
              <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/10 rounded-2xl p-8 mb-8 shadow-xl">
                <div className="prose prose-invert max-w-none">
                  <p className="text-white text-xl leading-relaxed font-medium whitespace-pre-wrap tracking-wide" style={{ lineHeight: '1.8' }}>
                    {currentQuestion.content || currentQuestion.title}
                  </p>
                </div>
                
                {/* Question Image if exists */}
                {currentQuestion.image_url && (
                  <div className="mt-6 p-4 bg-black/20 rounded-xl">
                    <img 
                      src={`${API}/api/company/enterprise/files/${currentQuestion.image_url}`}
                      alt="Question illustration"
                      className="max-h-64 rounded-lg mx-auto"
                    />
                  </div>
                )}
              </div>
              
              {/* Answer Input - ENHANCED */}
              <div className="bg-gradient-to-br from-slate-800/30 to-slate-900/30 border border-white/10 rounded-2xl p-8">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-5 flex items-center gap-2">
                  <CheckCircle size={16} className="text-cyan-400" />
                  Select Your Answer
                </h3>
                
                {/* Locked Question Notice */}
                {lockedQuestions[currentQuestion.id] && (
                  <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl flex items-center gap-3">
                    <Lock size={20} className="text-orange-400 flex-shrink-0" />
                    <p className="text-orange-400 text-sm">
                      This answer has been locked and cannot be changed. You can still review your response.
                    </p>
                  </div>
                )}
                
                {currentQuestion.type === 'mcq' && (
                  <div className="space-y-4">
                    {currentQuestion.options?.map((option, idx) => (
                      <label
                        key={idx}
                        className={`flex items-start gap-5 p-5 rounded-2xl border-2 transition-all group ${
                          lockedQuestions[currentQuestion.id]
                            ? 'cursor-not-allowed opacity-75'
                            : 'cursor-pointer'
                        } ${
                          answers[currentQuestion.id] === option
                            ? 'border-cyan-500 bg-cyan-500/10 shadow-lg shadow-cyan-500/10'
                            : 'border-white/10 hover:border-white/20 hover:bg-white/5'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`q_${currentQuestion.id}`}
                          value={option}
                          checked={answers[currentQuestion.id] === option}
                          onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                          disabled={lockedQuestions[currentQuestion.id]}
                          className="sr-only"
                        />
                        <span className={`w-10 h-10 rounded-xl border-2 flex items-center justify-center font-bold text-lg flex-shrink-0 transition-all ${
                          answers[currentQuestion.id] === option
                            ? 'border-cyan-500 bg-cyan-500 text-white shadow-lg shadow-cyan-500/30'
                            : 'border-slate-500 text-slate-400 group-hover:border-slate-400'
                        }`}>
                          {String.fromCharCode(65 + idx)}
                        </span>
                        <span className={`text-lg leading-relaxed pt-1.5 ${
                          answers[currentQuestion.id] === option ? 'text-white font-medium' : 'text-slate-300'
                        }`} style={{ lineHeight: '1.6' }}>
                          {option}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
                
                {currentQuestion.type === 'subjective' && (
                  <div>
                    <textarea
                      value={answers[currentQuestion.id] || ''}
                      onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                      rows={10}
                      disabled={lockedQuestions[currentQuestion.id]}
                      className={`w-full bg-slate-900/50 border-2 border-white/10 rounded-2xl p-6 text-white text-lg leading-relaxed focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 resize-none placeholder:text-slate-500 ${
                        lockedQuestions[currentQuestion.id] ? 'opacity-75 cursor-not-allowed' : ''
                      }`}
                      placeholder="Type your detailed answer here..."
                      style={{ lineHeight: '1.8' }}
                    />
                    <div className="mt-3 flex justify-between items-center text-sm text-slate-500">
                      <span>{lockedQuestions[currentQuestion.id] ? 'Answer locked' : 'Be clear and concise in your response'}</span>
                      <span>{(answers[currentQuestion.id] || '').split(/\s+/).filter(w => w).length} words</span>
                    </div>
                  </div>
                )}
                
                {currentQuestion.type === 'coding' && (
                  <div className="space-y-4">
                    {/* Problem Details */}
                    {(currentQuestion.input_format || currentQuestion.output_format || currentQuestion.constraints) && (
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        {currentQuestion.input_format && (
                          <div className="bg-slate-800/50 rounded-xl p-4 border border-white/10">
                            <h4 className="text-xs font-semibold text-cyan-400 uppercase tracking-wider mb-2">Input Format</h4>
                            <p className="text-slate-300 text-sm font-mono whitespace-pre-wrap">{currentQuestion.input_format}</p>
                          </div>
                        )}
                        {currentQuestion.output_format && (
                          <div className="bg-slate-800/50 rounded-xl p-4 border border-white/10">
                            <h4 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2">Output Format</h4>
                            <p className="text-slate-300 text-sm font-mono whitespace-pre-wrap">{currentQuestion.output_format}</p>
                          </div>
                        )}
                        {currentQuestion.constraints && (
                          <div className="bg-slate-800/50 rounded-xl p-4 border border-white/10">
                            <h4 className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2">Constraints</h4>
                            <p className="text-slate-300 text-sm font-mono whitespace-pre-wrap">{currentQuestion.constraints}</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Sample I/O */}
                    {(currentQuestion.sample_input || currentQuestion.sample_output) && (
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-slate-800/50 rounded-xl p-4 border border-white/10">
                          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Sample Input</h4>
                          <pre className="text-emerald-400 text-sm font-mono bg-slate-900/50 rounded-lg p-3 overflow-x-auto">{currentQuestion.sample_input || 'N/A'}</pre>
                        </div>
                        <div className="bg-slate-800/50 rounded-xl p-4 border border-white/10">
                          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Sample Output</h4>
                          <pre className="text-emerald-400 text-sm font-mono bg-slate-900/50 rounded-lg p-3 overflow-x-auto">{currentQuestion.sample_output || 'N/A'}</pre>
                        </div>
                      </div>
                    )}
                    
                    {/* Code Editor Header */}
                    <div className="flex items-center justify-between bg-slate-800/80 rounded-t-xl px-4 py-3 border border-white/10 border-b-0">
                      <div className="flex items-center gap-4">
                        <select
                          value={codeLanguage}
                          onChange={(e) => setCodeLanguage(e.target.value)}
                          className="bg-slate-700 text-white px-3 py-1.5 rounded-lg text-sm border border-white/10 focus:outline-none focus:border-cyan-500"
                        >
                          <option value="python">Python 3</option>
                          <option value="javascript">JavaScript</option>
                          <option value="java">Java</option>
                          <option value="cpp">C++</option>
                          <option value="c">C</option>
                          <option value="go">Go</option>
                          <option value="rust">Rust</option>
                        </select>
                        <span className="text-slate-500 text-sm">|</span>
                        <span className="text-slate-400 text-sm font-mono">{(answers[currentQuestion.id] || '').split('\n').length} lines</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => runCode('custom')}
                          disabled={codeRunning}
                          className="px-4 py-1.5 bg-slate-600 text-white rounded-lg text-sm hover:bg-slate-500 disabled:opacity-50 flex items-center gap-2 transition-all"
                        >
                          <Play size={14} />
                          Run
                        </button>
                        <button
                          onClick={() => runCode('test')}
                          disabled={codeRunning || !currentQuestion.test_cases?.length}
                          className="px-4 py-1.5 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-lg text-sm hover:from-emerald-500 hover:to-emerald-400 disabled:opacity-50 flex items-center gap-2 transition-all shadow-lg shadow-emerald-500/20"
                        >
                          {codeRunning ? (
                            <><Loader2 size={14} className="animate-spin" /> Running...</>
                          ) : (
                            <><CheckCircle size={14} /> Run Tests</>
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Monaco Code Editor */}
                    <div className="border-2 border-white/10 border-t-0 rounded-b-xl overflow-hidden" style={{ height: '400px' }}>
                      <Editor
                        height="100%"
                        language={codeLanguage === 'cpp' ? 'cpp' : codeLanguage === 'c' ? 'c' : codeLanguage}
                        theme="vs-dark"
                        value={answers[currentQuestion.id] || ''}
                        onChange={(value) => handleAnswerChange(currentQuestion.id, value || '')}
                        options={{
                          fontSize: 14,
                          fontFamily: "'JetBrains Mono', 'Fira Code', 'Monaco', monospace",
                          lineNumbers: 'on',
                          minimap: { enabled: false },
                          scrollBeyondLastLine: false,
                          automaticLayout: true,
                          tabSize: 4,
                          wordWrap: 'on',
                          padding: { top: 16, bottom: 16 },
                          suggestOnTriggerCharacters: true,
                          quickSuggestions: true,
                          folding: true,
                          lineDecorationsWidth: 10,
                          renderLineHighlight: 'all',
                          cursorBlinking: 'smooth',
                          smoothScrolling: true,
                          contextmenu: false,
                          formatOnPaste: true,
                          formatOnType: true
                        }}
                        loading={
                          <div className="flex items-center justify-center h-full bg-[#1e1e1e]">
                            <Loader2 className="animate-spin text-cyan-500" size={24} />
                            <span className="ml-2 text-slate-400">Loading editor...</span>
                          </div>
                        }
                      />
                    </div>
                    
                    {/* Custom Input */}
                    <div className="bg-slate-800/50 rounded-xl p-4 border border-white/10">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Custom Input</h4>
                        <span className="text-xs text-slate-500">Used when clicking "Run"</span>
                      </div>
                      <textarea
                        value={customInput}
                        onChange={(e) => setCustomInput(e.target.value)}
                        rows={3}
                        className="w-full bg-slate-900/50 border border-white/10 rounded-lg p-3 text-slate-300 font-mono text-sm focus:outline-none focus:border-cyan-500 resize-none"
                        placeholder="Enter custom input here..."
                      />
                    </div>
                    
                    {/* Output Panel */}
                    {codeOutput && (
                      <div className="bg-slate-800/50 rounded-xl border border-white/10 overflow-hidden">
                        <div className={`px-4 py-3 border-b border-white/10 flex items-center justify-between ${
                          codeOutput.all_passed ? 'bg-emerald-500/10' : 
                          codeOutput.success ? 'bg-slate-700/50' : 'bg-red-500/10'
                        }`}>
                          <h4 className={`text-sm font-semibold flex items-center gap-2 ${
                            codeOutput.all_passed ? 'text-emerald-400' : 
                            codeOutput.success ? 'text-white' : 'text-red-400'
                          }`}>
                            {codeOutput.all_passed ? (
                              <><CheckCircle size={16} /> All Tests Passed</>
                            ) : codeOutput.results ? (
                              <><AlertCircle size={16} /> {codeOutput.passed_count}/{codeOutput.total_count} Tests Passed</>
                            ) : codeOutput.success ? (
                              <><CheckCircle size={16} /> Execution Complete</>
                            ) : (
                              <><AlertTriangle size={16} /> {codeOutput.status || 'Error'}</>
                            )}
                          </h4>
                          {codeOutput.execution_time > 0 && (
                            <span className="text-xs text-slate-400">
                              Time: {codeOutput.execution_time}s | Memory: {Math.round((codeOutput.memory_used || 0) / 1024)}KB
                            </span>
                          )}
                        </div>
                        
                        <div className="p-4 max-h-64 overflow-y-auto">
                          {/* Test case results */}
                          {codeOutput.results && (
                            <div className="space-y-3">
                              {codeOutput.results.map((result, idx) => (
                                <div key={idx} className={`rounded-lg border p-3 ${
                                  result.passed 
                                    ? 'bg-emerald-500/5 border-emerald-500/20' 
                                    : 'bg-red-500/5 border-red-500/20'
                                }`}>
                                  <div className="flex items-center justify-between mb-2">
                                    <span className={`text-sm font-medium ${result.passed ? 'text-emerald-400' : 'text-red-400'}`}>
                                      {result.passed ? '✓' : '✗'} Test Case {result.test_case}
                                    </span>
                                    <span className="text-xs text-slate-500">{result.execution_time}s</span>
                                  </div>
                                  {result.input !== '[Hidden]' && (
                                    <div className="grid grid-cols-2 gap-3 text-xs">
                                      <div>
                                        <span className="text-slate-500 block mb-1">Input:</span>
                                        <pre className="bg-slate-900/50 rounded p-2 text-slate-300 font-mono overflow-x-auto">{result.input || '(empty)'}</pre>
                                      </div>
                                      <div>
                                        <span className="text-slate-500 block mb-1">Expected:</span>
                                        <pre className="bg-slate-900/50 rounded p-2 text-slate-300 font-mono overflow-x-auto">{result.expected_output || '(empty)'}</pre>
                                      </div>
                                    </div>
                                  )}
                                  {result.actual_output && (
                                    <div className="mt-2 text-xs">
                                      <span className="text-slate-500 block mb-1">Your Output:</span>
                                      <pre className={`bg-slate-900/50 rounded p-2 font-mono overflow-x-auto ${result.passed ? 'text-emerald-400' : 'text-red-400'}`}>{result.actual_output}</pre>
                                    </div>
                                  )}
                                  {result.error && (
                                    <div className="mt-2 text-xs">
                                      <span className="text-red-400 block mb-1">Error:</span>
                                      <pre className="bg-red-900/20 rounded p-2 text-red-300 font-mono overflow-x-auto">{result.error}</pre>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Simple output (for custom run) */}
                          {!codeOutput.results && (
                            <div>
                              {codeOutput.output && (
                                <div className="mb-3">
                                  <span className="text-slate-500 text-xs block mb-1">Output:</span>
                                  <pre className="bg-slate-900/50 rounded-lg p-3 text-emerald-400 font-mono text-sm overflow-x-auto whitespace-pre-wrap">{codeOutput.output}</pre>
                                </div>
                              )}
                              {codeOutput.error && (
                                <div>
                                  <span className="text-red-400 text-xs block mb-1">Error:</span>
                                  <pre className="bg-red-900/20 rounded-lg p-3 text-red-300 font-mono text-sm overflow-x-auto whitespace-pre-wrap">{codeOutput.error}</pre>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {(currentQuestion.type === 'numeric' || currentQuestion.type === 'fill_blank') && (
                  <input
                    type="text"
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                    className="w-full bg-slate-900/50 border-2 border-white/10 rounded-2xl p-5 text-white text-xl focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 placeholder:text-slate-500"
                    placeholder="Enter your answer..."
                  />
                )}
                
                {/* PDF Question Type */}
                {currentQuestion.type === 'pdf' && (
                  <div className="space-y-6">
                    {/* Debug info for PDF questions */}
                    {console.log('[PDF Question Debug]', {
                      type: currentQuestion.type,
                      pdf_url: currentQuestion.pdf_url,
                      pdf_file_name: currentQuestion.pdf_file_name,
                      full_url: currentQuestion.pdf_url ? (currentQuestion.pdf_url.startsWith('/') ? `${API}${currentQuestion.pdf_url}` : currentQuestion.pdf_url) : 'N/A',
                      API: API
                    })}
                    {/* PDF Question Paper Display */}
                    {currentQuestion.pdf_url ? (
                      <div className="bg-slate-800/50 rounded-xl border border-white/10 overflow-hidden">
                        <div className="px-4 py-3 border-b border-white/10 bg-slate-800/80 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FileText className="text-red-400" size={20} />
                            <div>
                              <h4 className="text-white font-medium">Question Paper</h4>
                              <p className="text-xs text-slate-400">{currentQuestion.pdf_file_name || 'PDF Document'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={currentQuestion.pdf_url.startsWith('/') ? `${API}${currentQuestion.pdf_url}` : currentQuestion.pdf_url}
                              download={currentQuestion.pdf_file_name || 'question.pdf'}
                              className="flex items-center gap-2 px-3 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors text-sm"
                            >
                              <Download size={16} />
                              Download
                            </a>
                            <a
                              href={currentQuestion.pdf_url.startsWith('/') ? `${API}${currentQuestion.pdf_url}` : currentQuestion.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 px-3 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors text-sm font-medium"
                            >
                              <ExternalLink size={16} />
                              Open in New Tab
                            </a>
                          </div>
                        </div>
                        <div className="p-4">
                          {/* Try iframe first, with object tag as fallback */}
                          <object
                            data={currentQuestion.pdf_url.startsWith('/') ? `${API}${currentQuestion.pdf_url}` : currentQuestion.pdf_url}
                            type="application/pdf"
                            className="w-full h-[500px] rounded-lg bg-white"
                          >
                            <iframe
                              src={currentQuestion.pdf_url.startsWith('/') ? `${API}${currentQuestion.pdf_url}#toolbar=1` : `${currentQuestion.pdf_url}#toolbar=1`}
                              className="w-full h-[500px] rounded-lg bg-white"
                              title="Question Paper PDF"
                            />
                          </object>
                        </div>
                        <div className="px-4 py-2 bg-slate-900/50 border-t border-white/5 text-center">
                          <p className="text-xs text-slate-500">
                            If the PDF doesn't load, use the "Download" or "Open in New Tab" buttons above
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 text-center">
                        <AlertCircle className="mx-auto text-amber-400 mb-3" size={32} />
                        <p className="text-amber-400 font-medium">Question paper not available</p>
                        <p className="text-sm text-slate-400 mt-1">Please contact the administrator if this is unexpected.</p>
                        {currentQuestion.content && (
                          <div className="mt-4 text-left bg-slate-800/50 rounded-lg p-4">
                            <p className="text-slate-300 whitespace-pre-wrap">{currentQuestion.content}</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Answer Upload Section */}
                    <div className="bg-slate-800/50 rounded-xl border border-white/10 p-6">
                      <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                        <Upload size={18} className="text-emerald-400" />
                        Upload Your Answer (PDF)
                      </h4>
                      
                      {pdfAnswerFiles[currentQuestion.id] ? (
                        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                                <CheckCircle className="text-emerald-400" size={20} />
                              </div>
                              <div>
                                <p className="text-emerald-400 font-medium">{pdfAnswerFiles[currentQuestion.id].filename}</p>
                                <p className="text-xs text-slate-400">Uploaded successfully</p>
                              </div>
                            </div>
                            <label className="cursor-pointer px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors text-sm">
                              Replace
                              <input
                                type="file"
                                accept="application/pdf"
                                className="hidden"
                                onChange={(e) => handlePdfAnswerUpload(currentQuestion.id, e.target.files[0])}
                                disabled={uploadingPdf}
                              />
                            </label>
                          </div>
                        </div>
                      ) : (
                        <label className={`block cursor-pointer border-2 border-dashed rounded-xl p-8 text-center transition-all ${uploadingPdf ? 'border-cyan-500 bg-cyan-500/5' : 'border-slate-600 hover:border-cyan-500 hover:bg-cyan-500/5'}`}>
                          <input
                            type="file"
                            accept="application/pdf"
                            className="hidden"
                            onChange={(e) => handlePdfAnswerUpload(currentQuestion.id, e.target.files[0])}
                            disabled={uploadingPdf}
                          />
                          {uploadingPdf ? (
                            <div className="flex flex-col items-center gap-3">
                              <Loader2 className="animate-spin text-cyan-400" size={32} />
                              <p className="text-slate-400">Uploading...</p>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-3">
                              <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center">
                                <Upload className="text-slate-400" size={28} />
                              </div>
                              <div>
                                <p className="text-white font-medium">Click to upload your answer</p>
                                <p className="text-sm text-slate-400 mt-1">PDF files only, max 10MB</p>
                              </div>
                            </div>
                          )}
                        </label>
                      )}
                      
                      {/* Text answer as fallback */}
                      {currentQuestion.allow_text_answer !== false && (
                        <div className="mt-6">
                          <p className="text-sm text-slate-400 mb-2">Or type your answer below:</p>
                          <textarea
                            value={answers[currentQuestion.id] || ''}
                            onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                            rows={8}
                            className="w-full bg-slate-900/50 border border-white/10 rounded-xl p-4 text-white focus:outline-none focus:border-cyan-500 resize-none"
                            placeholder="Type your detailed answer here..."
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Navigation - Enhanced */}
              <div className="flex items-center justify-between mt-8">
                {allowNavigation ? (
                  <button
                    onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                    disabled={currentIndex === 0}
                    className="px-6 py-3 bg-white/5 text-white rounded-xl flex items-center gap-3 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all border border-white/10 font-medium"
                    data-testid="prev-question-btn"
                  >
                    <ChevronLeft size={22} />
                    <span>Previous</span>
                  </button>
                ) : (
                  <div className="px-6 py-3 bg-slate-800/50 text-slate-500 rounded-xl flex items-center gap-3 border border-slate-700/50 font-medium cursor-not-allowed" title="Navigation disabled for this assessment">
                    <ChevronLeft size={22} />
                    <span>Previous</span>
                  </div>
                )}
                
                <div className="flex items-center gap-3 text-slate-400">
                  <span className="text-lg font-semibold text-white">{currentIndex + 1}</span>
                  <span>of</span>
                  <span className="text-lg font-semibold text-white">{questions.length}</span>
                </div>
                
                <button
                  onClick={moveToNextQuestion}
                  disabled={currentIndex === questions.length - 1}
                  className="px-6 py-3 bg-white/5 text-white rounded-xl flex items-center gap-3 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all border border-white/10 font-medium"
                  data-testid="next-question-btn"
                >
                  <span>Next</span>
                  <ChevronRight size={22} />
                  {lockAnswersOnProceed && !allowNavigation && (
                    <Lock size={14} className="text-orange-400" />
                  )}
                </button>
              </div>
            </div>
          )}
        </main>
        
        {/* Question Navigator Sidebar - Enhanced */}
        <aside className="w-80 bg-slate-900/50 border-l border-white/5 p-6 overflow-y-auto">
          <h3 className="text-white font-bold mb-5 text-base flex items-center gap-2">
            <div className="w-2 h-2 bg-cyan-400 rounded-full" />
            Question Navigator
          </h3>
          
          {/* Sequential mode warning */}
          {!allowNavigation && (
            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
              <p className="text-xs text-amber-400 flex items-center gap-2">
                <AlertTriangle size={14} />
                Sequential mode: You can only proceed forward
              </p>
            </div>
          )}
          
          {/* Lock answers warning */}
          {lockAnswersOnProceed && !allowNavigation && (
            <div className="mb-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-xl">
              <p className="text-xs text-orange-400 flex items-center gap-2">
                <Lock size={14} />
                Strict mode: Answers lock when you proceed
              </p>
            </div>
          )}
          
          <div className="grid grid-cols-5 gap-2.5">
            {questions.map((q, idx) => {
              // In sequential mode:
              // - Can only click on the NEXT question (currentIndex + 1)
              // - Cannot go back or jump ahead
              const isCurrentQuestion = idx === currentIndex;
              const isNextQuestion = idx === currentIndex + 1;
              const canClick = allowNavigation || isCurrentQuestion || isNextQuestion;
              const isLocked = lockedQuestions[q.id];
              const isPreviousQuestion = idx < currentIndex;
              const isFutureQuestion = idx > currentIndex + 1;
              
              return (
                <button
                  key={q.id}
                  onClick={() => moveToQuestion(idx)}
                  disabled={!allowNavigation && !isCurrentQuestion && !isNextQuestion}
                  className={`w-11 h-11 rounded-xl font-semibold text-sm transition-all relative border ${
                    isCurrentQuestion
                      ? 'bg-gradient-to-br from-cyan-500 to-blue-500 text-white border-cyan-400 shadow-lg shadow-cyan-500/30'
                      : isLocked
                      ? 'bg-orange-500/20 text-orange-400 border-orange-500/30 cursor-not-allowed'
                      : !allowNavigation && isPreviousQuestion
                      ? 'bg-slate-800/50 text-slate-600 border-slate-700/50 cursor-not-allowed'
                      : !allowNavigation && isFutureQuestion
                      ? 'bg-slate-800/30 text-slate-700 border-slate-800/50 cursor-not-allowed'
                      : answers[q.id]
                      ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/30'
                      : 'bg-white/5 text-slate-400 border-white/10 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`question-nav-${idx + 1}`}
                >
                  {idx + 1}
                  {isLocked && (
                    <Lock size={8} className="absolute -top-1 -right-1 text-orange-400" />
                  )}
                  {flagged[q.id] && !isLocked && (
                    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-amber-500 rounded-full border-2 border-slate-900" />
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Legend - Enhanced */}
          <div className="mt-8 space-y-3">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Legend</h4>
            <div className="flex items-center gap-3">
              <span className="w-5 h-5 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg" />
              <span className="text-slate-400 text-sm">Current Question</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-5 h-5 bg-emerald-500/20 border border-emerald-500/30 rounded-lg" />
              <span className="text-slate-400 text-sm">Answered</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-5 h-5 bg-white/5 border border-white/10 rounded-lg" />
              <span className="text-slate-400 text-sm">Not Answered</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-5 h-5 bg-amber-500 rounded-full" />
              <span className="text-slate-400 text-sm">Flagged for Review</span>
            </div>
            {lockAnswersOnProceed && (
              <div className="flex items-center gap-3">
                <span className="w-5 h-5 bg-orange-500/20 border border-orange-500/30 rounded-lg flex items-center justify-center">
                  <Lock size={10} className="text-orange-400" />
                </span>
                <span className="text-slate-400 text-sm">Answer Locked</span>
              </div>
            )}
          </div>
          
          {/* Proctoring Status - Enhanced */}
          <div className="mt-8 p-5 bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-2xl border border-white/10">
            <h4 className="text-white font-semibold mb-4 flex items-center gap-2 text-sm">
              <Shield size={18} className="text-cyan-400" />
              Proctoring Status
            </h4>
            
            {/* AI Camera */}
            <div className="mb-4">
              <AIProctoringCamera
                enabled={aiProctoringEnabled && !submitted}
                onViolation={handleAIViolation}
                onStatusChange={handleAIProctoringStatus}
                violationThreshold={10}
                warningThreshold={5}
                captureOnViolation={true}
                attemptId={attemptData?.attempt_id}
                challengeId={challengeId}
              />
            </div>
            
            {/* Audio Monitoring */}
            <div className="mb-4">
              <AudioProctoring
                enabled={!submitted}
                sensitivity={audioSensitivity}
                onViolation={handleAudioViolation}
                onStatusChange={handleAudioStatus}
                violationThreshold={10}
                attemptId={attemptData?.attempt_id}
                challengeId={challengeId}
              />
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-2.5 bg-white/5 rounded-lg">
                <span className="text-slate-400 text-sm">Fullscreen</span>
                <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                  isFullscreen ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {isFullscreen ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center justify-between p-2.5 bg-white/5 rounded-lg">
                <span className="text-slate-400 text-sm">System Violations</span>
                <span className={`text-sm font-semibold ${violations.length > 5 ? 'text-red-400' : 'text-slate-300'}`}>
                  {violations.length}/10
                </span>
              </div>
              <div className="flex items-center justify-between p-2.5 bg-white/5 rounded-lg">
                <span className="text-slate-400 text-sm">AI Violations</span>
                <span className={`text-sm font-semibold ${aiViolations > 5 ? 'text-red-400' : 'text-slate-300'}`}>
                  {aiViolations}/10
                </span>
              </div>
              <div className="flex items-center justify-between p-2.5 bg-white/5 rounded-lg">
                <span className="text-slate-400 text-sm">Audio Violations</span>
                <span className={`text-sm font-semibold ${audioViolations > 5 ? 'text-red-400' : 'text-slate-300'}`}>
                  {audioViolations}/10
                </span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default EnterpriseAssessmentPage;
